import React, { useMemo, useState } from 'react';
import {
  validateActivityPayload,
  InflationLiveStage,
  type InflationMarketPayload,
} from '@interactive-room';
import { emptySandboxPayload } from '../emptyPayloads';
import {
  createDraftId,
  saveActivityDraft,
} from '../activityStorage';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import type { StudioLiveEditorProps } from '../types/editor.types';

function editorRuntime(payload: InflationMarketPayload): InflationMarketRuntime {
  return {
    kind: 'inflation-market',
    currentPrice: payload.basePrice,
    history: [],
  };
}

export function InflationLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptySandboxPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const initial = launch.payload;
  const [payload, setPayload] = useState<InflationMarketPayload>(() =>
    initial.variant === 'inflation-market'
      ? (initial as InflationMarketPayload)
      : {
          variant: 'inflation-market',
          title: 'Mercado inflacionario',
          productName: 'Índice de precios',
          basePrice: 100_000,
          targetPrice: 118_800,
          roundDurationSec: 60,
          currencySymbol: '$',
          teams: [
            {
              teamId: 'team_1',
              label: 'Inflacionistas',
              modifiers: [
                { id: 'up10', label: '+10%', percent: 10 },
                { id: 'up20', label: '+20%', percent: 20 },
              ],
            },
            {
              teamId: 'team_2',
              label: 'Deflacionistas',
              modifiers: [
                { id: 'down10', label: '−10%', percent: -10 },
                { id: 'down15', label: '−15%', percent: -15 },
              ],
            },
          ],
        },
  );
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [error, setError] = useState<string | null>(null);
  const [savedId] = useState(draftId ?? createDraftId());
  const runtime = useMemo(() => editorRuntime(payload), [payload]);

  const save = () => {
    try {
      validateActivityPayload('sandbox', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'sandbox',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'historia',
          payload,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  return (
    <StudioLiveShell
      embedded={embedded}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
    >
      <div className="plugin-live-fill flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <InflationLiveStage
          payload={payload}
          runtime={runtime}
          mode="editor"
          fill
          onPayloadChange={(patch) => setPayload((prev) => ({ ...prev, ...patch }))}
        />
      </div>
    </StudioLiveShell>
  );
}
